import moment from 'moment';
import type {Moment} from 'moment';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import trim from 'lodash/trim';
import reduce from 'lodash/reduce';

import {formatNumber} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {NAME_PROPERTIES, ProfileNameProperties} from 'common/util/profiles';
import {COUNTRY_CODES} from 'common/util/country-codes';
import {isURL} from 'common/util/url';
import {formatDate} from 'common/util/date';
import {renameProperty} from 'common/report/util/properties';
import {MPProperties} from 'common/constants';
import {assertUnreachable} from 'common/util/typescript';

export const FULL_DISPLAY_DATE_FORMAT = `h:mm a, ddd, MMM D, YYYY`;
export const SECOND_DISPLAY_DATE_FORMAT = `h:mm:ss a, ddd, MMM D, YYYY`;
export const MILLISECOND_DISPLAY_DATE_FORMAT = `h:mm:ss.SSS a, ddd, MMM D, YYYY`;

export enum PropertyRenderType {
  Email = `email`,
  Link = `link`,
  String = `string`,
}

export type ValidPropertyValue = string | number | boolean | any[];

interface BaseRenderProperty {
  raw: ValidPropertyValue;
  type: PropertyRenderType;
  renderString: string;
}

export interface RenderEmailProperty extends BaseRenderProperty {
  raw: string;
  type: PropertyRenderType.Email;
  href: string;
  title: string;
}

export interface RenderLinkProperty extends BaseRenderProperty {
  raw: string;
  type: PropertyRenderType.Link;
  href: string;
  title: string;
}

export interface RenderStringProperty extends BaseRenderProperty {
  raw: string | number;
  type: PropertyRenderType.String;
  title?: string;
}

export interface RenderTransactionProperty extends BaseRenderProperty {
  raw: Array<{$amount: number}> | string;
  type: PropertyRenderType.String;
}

export interface RenderNumberProperty extends BaseRenderProperty {
  raw: number;
  type: PropertyRenderType.String;
}

type RenderValue =
  | RenderEmailProperty
  | RenderLinkProperty
  | RenderStringProperty
  | RenderNumberProperty
  | RenderTransactionProperty;

export interface FormattedProperty {
  name: {
    formatted: string;
    raw: string;
  };
  renderValue: RenderValue;
}

export const getRawPropertyValue = (prop: FormattedProperty): ValidPropertyValue => prop?.renderValue?.raw;
export const getRenderInputPropertyValue = (prop: FormattedProperty): string => prop?.renderValue?.renderString;
export const getFormattedPropertyName = (prop: FormattedProperty): string => prop?.name?.formatted;
export const getRawPropertyName = (prop: FormattedProperty): string => prop?.name?.raw;

export const OBFUSCATED_STRING = `******`;
export const OBFUSCATED_TOOLTIP = `Disabled due to Classified data`;

export function formatCountry(countryCode) {
  return COUNTRY_CODES[countryCode.toUpperCase()] ?? countryCode;
}

function formatAgo(value, utcOffset) {
  let ret = value;

  // We receive a timestamp in project time that gets parsed as the local timezone
  // We need to save this original time for displaying in the UI, but X hours ago calculations need to be done relative to local time
  // so we have to convert the received timestamp into the equivalent local time
  const momentInProjectTime = moment(value, moment.ISO_8601, true);
  if (momentInProjectTime.isValid()) {
    const momentInUTC = momentInProjectTime.subtract(utcOffset, `minutes`); // add the utc offset for our project (project time -> utc time)

    const momentNowUTC = moment().add(new Date().getTimezoneOffset(), `minutes`); // remove current browser timezone offset (local -> utc time)
    const weeksAgo = momentNowUTC.clone().subtract(2, `weeks`);

    if (momentInUTC.isAfter(weeksAgo)) {
      ret = momentInUTC.from(momentNowUTC);
    } else {
      ret = momentInProjectTime.format(`MMM D, YYYY`);
    }
  }

  return ret;
}

function formatDateMoment(value: Moment, format: string = FULL_DISPLAY_DATE_FORMAT, utc = false): string {
  return formatDate(value.toDate(), {
    unit: `full`,
    utc,
    customFormatting: {
      full: format,
    },
  });
}

/**
 * Timestamps (i.e. seconds or MS from epoch time) must be treated *differently* from ISO8601 strings
 * This is because our ISO8601 strings are in the format `YYYY-MM-DDTHH:MM:SSZ`, and the `Z` at the end
 * makes moment treat them as UTC and not include local timezone manipulation. However, time *stamps*
 * do not have this luxury, and we must use the utcoffset to tell moment how much to offset by for project time.
 *
 * @param value - a timestamp in milliseconds from epoch time (if you have seconds, multiply by 1000)
 * @param utcOffset - minutes from utc
 */
export function epochTimestampToProjectTime(value: number, utcOffset: number): Moment {
  const time = moment.utc(value);
  time.add(utcOffset, `minutes`);
  return time;
}

/**
 * Transforms a single transaction, a list of transactions, or a json string representing one of the previous into a single number
 */
function formatTransactions(transactions: Array<{$amount: number}> | {$amount: number} | string): number {
  // Event properties can have JSON transaction properties
  if (isString(transactions)) {
    transactions = JSON.parse(transactions);
  }

  // Sometimes transactions shows up as a single transaction (or parsed from above)
  if (isPlainObject(transactions)) {
    transactions = [transactions as {$amount: number}];
  }

  if (!isArray(transactions)) {
    throw new Error(`Transactions was not an array of $amount values; could not format.\n${transactions}`);
  }

  return transactions.reduce((acc, transaction) => acc + (isNumber(transaction?.$amount) ? transaction.$amount : 0), 0);
}

const defaultRenderValue = (value: string): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: trim(String(value)),
});

const renderEpochTime = ({unit}: {unit: `seconds` | `milliseconds`}) => (
  value: number,
  {utcOffset},
): RenderStringProperty => {
  let renderString;
  switch (unit) {
    case `seconds`:
      renderString = formatDateMoment(
        epochTimestampToProjectTime(value * 1000, utcOffset),
        SECOND_DISPLAY_DATE_FORMAT,
        true,
      );
      break;
    case `milliseconds`:
      renderString = formatDateMoment(
        epochTimestampToProjectTime(value, utcOffset),
        MILLISECOND_DISPLAY_DATE_FORMAT,
        true,
      );
      break;
    default:
      assertUnreachable(unit);
  }

  return {
    raw: value,
    type: PropertyRenderType.String,
    renderString,
  };
};

const renderTransaction = (value: any[] | string): RenderTransactionProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: formatNumber(formatTransactions(value)),
});

const renderEmail = (value: string): RenderEmailProperty => {
  const trimmedValue = trim(value);

  return {
    raw: value,
    type: PropertyRenderType.Email,
    renderString: trimmedValue,
    href: `mailto:${value}`,
    title: trimmedValue === OBFUSCATED_STRING ? OBFUSCATED_TOOLTIP : trimmedValue,
  };
};

const renderUrl = (value: string): RenderLinkProperty => {
  const shortened = truncateMiddle(value, 60);

  let href = value;
  if (!href.includes(`://`)) {
    href = `http://` + href;
  }

  return {
    raw: value,
    type: PropertyRenderType.Link,
    renderString: shortened,
    href,
    title: value === OBFUSCATED_STRING ? OBFUSCATED_TOOLTIP : value,
  };
};

const renderLastSeen = (value: string, {utcOffset}): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: formatAgo(value, utcOffset),
  title: `Updated at ${formatDateMoment(moment(value, moment.ISO_8601, true))}`,
});

const renderCountry = (value: string): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: formatCountry(value),
});

const renderDate = (value: string): RenderStringProperty => {
  const maybeDate = moment(value, `YYYY-MM-DDTHH:mm:ss`, true);
  if (maybeDate.isValid()) {
    return {
      raw: value,
      type: PropertyRenderType.String,
      renderString: formatDateMoment(maybeDate),
    };
  }

  // False alarm, this wasn't a date, so return a normal string property
  return defaultRenderValue(value);
};

const renderObfuscated = (value: string): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: value,
  title: OBFUSCATED_TOOLTIP,
});

const renderJSON = (value: any): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: JSON.stringify(value),
});

type PropertyTester = (propertyName: string, propertyValue: ValidPropertyValue) => boolean;
type PropertyFormatter = (
  propertyValue: ValidPropertyValue,
  opts: {
    utcOffset: number;
  },
) => RenderValue;

const PROPERTY_FORMATTERS: Array<{
  test: string | PropertyTester;
  formatter: PropertyFormatter;
}> = [
  {test: MPProperties.Time, formatter: renderEpochTime({unit: `seconds`})},
  {test: MPProperties.MPProcessingTime, formatter: renderEpochTime({unit: `milliseconds`})},
  {test: MPProperties.Transactions, formatter: renderTransaction},
  {test: (propertyName: string, _) => /^\$?email$/i.test(propertyName), formatter: renderEmail},
  {test: (_, propertyValue) => isURL(propertyValue), formatter: renderUrl},
  {test: MPProperties.LastSeen, formatter: renderLastSeen},
  {test: MPProperties.CountryCode, formatter: renderCountry},
  {test: (_: string, propertyValue: string) => propertyValue === OBFUSCATED_STRING, formatter: renderObfuscated},
  {
    test: (_: string, propertyValue: string) => isPlainObject(propertyValue) || isArray(propertyValue),
    formatter: renderJSON,
  },
  // Always have the date formatter next to last, since it's the most "expensive" one and has a bail out condition for 'almost' dates
  {
    test: (_: string, propertyValue: string) => {
      // Since `moment()` is very expensive (can be ~30% of our perf time in this function), attempt parse as a date
      // only if it matches an initial date check
      const maybeDate = new Date(propertyValue);

      // stolen from https://stackoverflow.com/a/10589791
      return maybeDate instanceof Date && !isNaN(maybeDate.valueOf());
    },
    formatter: renderDate,
  },
  {test: () => true, formatter: defaultRenderValue},
];

interface FormatPropertyOptions {
  // Needed for renaming property names
  dataGroupId?: string;

  // needed ONLY for formatting `last seen`. DO NOT use for formatting dates; we want to display
  // everything in project time, so make no adjustments to dates (i.e.  2020-08-03T10:00:00 => 10am)
  utcOffset?: number;
}

export function formatProperty(
  propertyName: string,
  propertyValue: any,
  {dataGroupId, utcOffset}: FormatPropertyOptions,
): FormattedProperty {
  const name = {
    formatted: renameProperty(propertyName, {dataGroupId}),
    raw: propertyName,
  };

  if (!propertyValue) {
    return {
      name,
      renderValue: defaultRenderValue(propertyValue),
    };
  }

  const {formatter} = PROPERTY_FORMATTERS.find(({test}) =>
    isString(test) ? propertyName === test : test(propertyName, propertyValue),
  );

  return {
    name,
    renderValue: formatter(propertyValue, {
      utcOffset,
    }),
  };
}

export function formatUserProperties(
  properties: {[name: string]: any},
  utcOffset: number,
): Map<string, FormattedProperty> {
  return Object.entries(properties).reduce((acc, [propertyName, propertyValue]: [string, any]): Map<
    string,
    FormattedProperty
  > => {
    acc.set(propertyName, formatProperty(propertyName, propertyValue, {utcOffset}));

    return acc;
  }, new Map());
}

export function getNameProperties(properties: Map<string, FormattedProperty>): ProfileNameProperties {
  return NAME_PROPERTIES.reduce((profile, propertyName) => {
    profile[propertyName] = getRawPropertyValue(properties.get(propertyName)) as string;
    return profile;
  }, {});
}

export function unformatUserProperties(properties: {
  [name: string]: FormattedProperty;
}): {[name: string]: ValidPropertyValue} {
  return reduce(
    properties,
    (acc, prop) => {
      acc[getRawPropertyName(prop)] = getRawPropertyValue(prop);
      return acc;
    },
    {},
  );
}
